import { useApolloClient } from '@apollo/react-hooks';
import XLSX from 'xlsx';

import { SYLLABUS_MODE } from 'Domains/syllabus';

import {
  RAW_CONTENT_TYPE,
  CONTENT_TYPE,
  SINGLE_MODULE_STATUS_TH,
  RANK_STATUS,
  RANK_STATUS_TH,
  formatWatchedPercent,
} from '../module';
import {
  getIndividualUnitReport,
  getTKIndividualUnitReport,
} from '../../GraphQL/queries/report.query';
import { formatSchoolTerm } from '../utils';

const getQuizArchetypesScoreAndTotal = (index, quizArchetypes) => {
  let score = quizArchetypes?.[index]?.score;
  const total = quizArchetypes?.[index]?.total;

  return { score, total };
};

export const convertIndividualUnitReportToAOA = (
  individualUnitReport,
  mode = SYLLABUS_MODE.TRADITIONAL
) => {
  const {
    schoolTerm: { shortName: schoolTerm },
    schoolGrade: { shortName: schoolGrade },
    subject: { name: subject },
    classNo,

    courseName,

    studentNo,
    classroomUsername,
    firstName,
    lastName,
    reports,
  } = individualUnitReport;

  return reports.map(report => {
    const {
      sectionName,
      sectionGroupName,
      subsectionName,
      contentType,
      status,
      watchedPercent,
      quizResult: rawQuizResult,
      quizArchetypes,
    } = report;

    let quizResult = ['', '', ''];
    let quizArchetypesResult = ['', '', '', ''];
    if (contentType === 'Quiz' && rawQuizResult) {
      const { score, fullScore, rank } = rawQuizResult;
      if (rank !== RANK_STATUS.UNRANK) {
        const quizResultPercentage = (score / fullScore) * 100;
        quizResult = [`${score}/${fullScore}`, `${quizResultPercentage}%`, RANK_STATUS_TH[rank]];
        if (quizArchetypes) {
          quizArchetypesResult = Array.from({ length: 4 }, (_, i) => {
            const { score, total } = getQuizArchetypesScoreAndTotal(i, quizArchetypes);
            return total > 0 ? `${score}/${total}` : '';
          });
        }
      } else {
        quizResult = ['', '', RANK_STATUS_TH[RANK_STATUS.UNRANK]];
      }
    }

    // % of the video watched; quiz rows stay blank.
    const watchedPercentResult =
      contentType === RAW_CONTENT_TYPE.VDO ? formatWatchedPercent(watchedPercent) : '';

    const traditionalReportResult = [
      schoolTerm,
      schoolGrade,
      `${schoolGrade}/${classNo}`,
      subject,
      studentNo,
      classroomUsername,
      `${firstName} ${lastName}`,
      courseName,
      sectionName,
      subsectionName,
      contentType === RAW_CONTENT_TYPE.QUIZ ? CONTENT_TYPE.QUIZ : CONTENT_TYPE.VDO,
      SINGLE_MODULE_STATUS_TH[status],
      watchedPercentResult,
      ...quizResult,
    ];

    const tkReportResult = [
      schoolTerm,
      schoolGrade,
      `${schoolGrade}/${classNo}`,
      subject,
      studentNo,
      classroomUsername,
      `${firstName} ${lastName}`,
      courseName,
      sectionName,
      sectionGroupName,
      subsectionName,
      contentType === RAW_CONTENT_TYPE.QUIZ ? CONTENT_TYPE.QUIZ : CONTENT_TYPE.VDO,
      SINGLE_MODULE_STATUS_TH[status],
      watchedPercentResult,
      ...quizResult,
      ...quizArchetypesResult,
    ];

    return mode === SYLLABUS_MODE.TRADITIONAL ? traditionalReportResult : tkReportResult;
  });
};

const formatReportToAOA = (individualUnitReport, mode) => {
  const formattedReportAOA = [
    ['ข้อมูลการเข้าเรียนและการทำข้อสอบของนักเรียนรายหน่วย'],
    [
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
    ],
    [
      'เทอม',
      'ระดับชั้น',
      'ห้อง',
      'วิชา',
      'เลขที่',
      'รหัสผู้ใช้',
      'ชื่อ-นามสกุล',
      'หน่วย',
      'บท',
      'เรื่อง',
      'ประเภทไฟล์',
      'สถานะการเข้าเรียน',
      '% ที่ดู VDO',
      'คะแนน',
      '%',
      'ผลสอบ',
    ],
  ];

  const formattedTKReportAOA = [
    ['ข้อมูลการเข้าเรียนและการทำข้อสอบของนักเรียนรายหน่วย'],
    [
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
      'ผลการทำข้อสอบ',
    ],
    [
      'เทอม',
      'ระดับชั้น',
      'ห้อง',
      'วิชา',
      'เลขที่',
      'รหัสผู้ใช้',
      'ชื่อ-นามสกุล',
      'หน่วย',
      'บท',
      'ภารกิจ',
      'เรื่อง',
      'ประเภทไฟล์',
      'สถานะการเข้าเรียน',
      '% ที่ดู VDO',
      'คะแนน',
      '%',
      'ผลสอบ',
      'เสริมความมั่นใจ',
      'เช็คความเข้าใจ',
      'ลดความเข้าใจผิด',
      'พร้อมสอบหรือยัง',
    ],
  ];

  const individualUnitReportsAOA = convertIndividualUnitReportToAOA(individualUnitReport, mode);

  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    formattedReportAOA.push(...individualUnitReportsAOA);
  } else {
    formattedTKReportAOA.push(...individualUnitReportsAOA);
  }

  return mode === SYLLABUS_MODE.TRADITIONAL ? formattedReportAOA : formattedTKReportAOA;
};

const createAndFormatWorksheet = (formattedReportAOA, mode) => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedReportAOA);
  const cols =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          { wch: 10 }, // เทอม
          { wch: 10 }, // ระดับชั้น
          { wch: 10 }, // ห้อง
          { wch: 15 }, // วิชา
          { wch: 10 }, // เลขที่
          { wch: 15 }, // รหัสผู้ใช้
          { wch: 15 }, // ชื่อ-นามสกุล
          { wch: 15 }, // หน่วย
          { wch: 15 }, // บท
          { wch: 15 }, // เรื่อง
          { wch: 10 }, // ประเภทไฟล์
          { wch: 10 }, // สถานะการเข้าเรียน
          { wch: 10 }, // % ที่ดู VDO
          { wch: 10 }, // คะแนน
          { wch: 10 }, // %
          { wch: 10 }, // ผลสอบ
        ]
      : [
          { wch: 10 }, // เทอม
          { wch: 10 }, // ระดับชั้น
          { wch: 10 }, // ห้อง
          { wch: 15 }, // วิชา
          { wch: 10 }, // เลขที่
          { wch: 15 }, // รหัสผู้ใช้
          { wch: 15 }, // ชื่อ-นามสกุล
          { wch: 15 }, // หน่วย
          { wch: 15 }, // บท
          { wch: 15 }, // ภารกิจ
          { wch: 15 }, // เรื่อง
          { wch: 10 }, // ประเภทไฟล์
          { wch: 10 }, // สถานะการเข้าเรียน
          { wch: 10 }, // % ที่ดู VDO
          { wch: 10 }, // คะแนน
          { wch: 10 }, // %
          { wch: 10 }, // ผลสอบ
          { wch: 10 }, // เสริมความมั่นใจ
          { wch: 10 }, // เช็คความจำ
          { wch: 10 }, // ลดความเข้าใจผิด
          { wch: 10 }, // พร้อมสอบ
        ];
  worksheet['!merges'] =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          { s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } }, // ภาพรวมการเข้าเรียนและการทำข้อสอบรายหน่วยของนักเรียน
          { s: { r: 1, c: 13 }, e: { r: 1, c: 15 } }, // ผลการทำข้อสอบ
        ]
      : [
          { s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } }, // ภาพรวมการเข้าเรียนและการทำข้อสอบรายหน่วยของนักเรียน
          { s: { r: 1, c: 14 }, e: { r: 1, c: 20 } }, // ผลการทำข้อสอบ
        ];
  worksheet['!cols'] = cols;

  return worksheet;
};

const ExportIndividualUnitReportData = props => {
  const {
    schoolTermId,
    schoolGradeId,
    classNo,
    subjectId,
    courseCode,
    classroomUsername,
    schoolCode,
    children,
    mode,
  } = props;

  const client = useApolloClient();

  const fetchIndividualUnitReportData = async () => {
    try {
      const { data } = await client.query({
        query:
          mode === SYLLABUS_MODE.TRADITIONAL ? getIndividualUnitReport : getTKIndividualUnitReport,
        variables: {
          schoolTermId,
          schoolGradeId,
          classNo,
          subjectId,
          courseCode,
          classroomUsername,
          schoolCode,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const exportIndividualUnitReport = async ({ report, type }) => {
    const {
      schoolTerm: { shortName: schoolTerm } = {},
      schoolGrade: { shortName: schoolGrade } = {},
      classNo,
      subject: { name: subject } = {},
      courseName,
      firstName,
      lastName,
    } = report;
    const formattedReportAOA = formatReportToAOA(report, mode);
    const worksheet = createAndFormatWorksheet(formattedReportAOA, mode);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ข้อมูลนักเรียนรายหน่วย');
    XLSX.writeFile(
      workbook,
      `${firstName}-${lastName}_${formatSchoolTerm(
        schoolTerm
      )}_${schoolGrade}-${classNo}_${subject}_${courseName}.` + (type || 'xlsx')
    );
  };

  return children({ fetchIndividualUnitReportData, exportIndividualUnitReport });
};

export default ExportIndividualUnitReportData;
